import { useCallback } from 'react';
import { getIn } from 'formik';

import { FInput } from '../../../components/baseV2';
import FormFieldReadOnlyValue from './FormFieldReadOnlyValue';
import { useFormReadOnly } from './formReadOnlyProvider';
import { numberInputStyleWithoutArrow } from '../../../constants';
import { DISABLE_BROWSER_AUTOFILL } from '../constants';
import { NUMBER_FIELD_LEFT_ALIGN_SX } from '../styles';

import type { ChangeEvent, FocusEvent } from 'react';
import type { FormikValues } from 'formik';
import type { FormNumberFieldProps } from '../types';

export default function FormNumberField<TValues extends FormikValues>({
  formik,
  name,
  label,
  placeholder = `Enter ${label.toLowerCase()}`,
  required = false,
  disabled = false,
  readOnly = false,
  min,
  max,
  step,
  maxDecimalPlaces,
  showRequiredIndicator,
  inputAriaLabel,
  sx,
  onBlur,
  onChange,
}: FormNumberFieldProps<TValues>) {
  const readOnlyFromContext = useFormReadOnly();
  const isReadOnly = readOnly || readOnlyFromContext;
  const fieldError = getIn(formik.errors, name);
  const isTouched = Boolean(getIn(formik.touched, name));
  const errorMessage = isTouched ? fieldError?.toString() : undefined;
  // Suppress the redundant "required field" message for empty required fields; the field still
  // highlights via `error` and its required indicator. Other validation messages still show.
  const helperText = required && !getIn(formik.values, name) ? undefined : errorMessage;

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      formik.handleBlur(event);
      onBlur?.(name);
    },
    [formik, name, onBlur],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      const decimalIndex = rawValue.indexOf('.');
      let nextValue = rawValue;
      if (maxDecimalPlaces != null && decimalIndex !== -1) {
        nextValue = rawValue.slice(
          0,
          maxDecimalPlaces === 0 ? decimalIndex : decimalIndex + 1 + maxDecimalPlaces,
        );
      }
      // Formik's handleChange parses type="number" inputs into JS numbers; store the raw
      // string instead so values stay string-typed for schemas, payload mappers, and
      // read-only rendering.
      void formik.setFieldValue(name, nextValue);
      onChange?.(name, nextValue);
    },
    [formik, maxDecimalPlaces, name, onChange],
  );

  if (isReadOnly) {
    return (
      <FormFieldReadOnlyValue
        label={label}
        required={required}
        value={getIn(formik.values, name) ?? ''}
      />
    );
  }

  return (
    <FInput
      disabled={disabled}
      error={Boolean(errorMessage)}
      fullWidth
      helperText={helperText}
      inputProps={{
        min,
        max,
        step,
        inputMode: maxDecimalPlaces != null ? 'decimal' : 'numeric',
        autoComplete: DISABLE_BROWSER_AUTOFILL,
        'aria-label': inputAriaLabel,
      }}
      label={label}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      showRequiredIndicator={showRequiredIndicator}
      sx={[
        numberInputStyleWithoutArrow,
        NUMBER_FIELD_LEFT_ALIGN_SX,
        ...(Array.isArray(sx) ? sx : [sx ?? {}]),
      ]}
      type="number"
      value={getIn(formik.values, name) ?? ''}
    />
  );
}
